/**
 * The small set of pieces every screen is built from.
 *
 * Hand-rolled rather than pulled from a library: the console needs about eight
 * primitives, and a dependency that ships a hundred would still have to be
 * restyled to reach the density this tool needs.
 */

import { useId } from 'react';
import type { ReactNode } from 'react';
import { ApiError, type ClaimStatus } from './api.js';
import { urgencyGlyph, urgencyText, urgencyTier, type UrgencyTier } from './urgency.js';
import { formatDate } from './format.js';

export type CalloutTone = 'info' | 'warn' | 'danger' | 'good';

export function Callout(props: {
  tone?: CalloutTone;
  title?: string;
  children: ReactNode;
  /** Announce to screen readers as it appears — used for anything the operator must act on. */
  live?: boolean;
}): ReactNode {
  const tone = props.tone ?? 'info';
  return (
    <div
      className={`callout callout-${tone}`}
      role={props.live ? 'alert' : undefined}
      aria-live={props.live ? 'assertive' : undefined}
    >
      <div>
        {props.title ? <strong className="callout-title">{props.title}</strong> : null}
        {props.children}
      </div>
    </div>
  );
}

/**
 * A failed API call, framed by what its status code actually means.
 *
 * The message is the server's, unaltered — see the header of `api.ts`. Only the
 * heading around it is ours, and it exists to stop a 409 reading as a form
 * validation error: the double-file guard is a safety rail that has just
 * prevented a filing which would have jeopardised two claims, not a typo.
 */
export function ErrorCallout({ error }: { error: unknown }): ReactNode {
  if (!(error instanceof ApiError)) {
    const message = error instanceof Error ? error.message : String(error);
    return (
      <Callout tone="danger" title="Could not reach the API" live>
        <p>{message}</p>
        <p className="faint">The server may be down, or the dev proxy may not be running.</p>
      </Callout>
    );
  }

  if (error.isGuard) {
    return (
      <Callout tone="danger" title="Blocked — this would be a duplicate filing" live>
        <p>{error.message}</p>
      </Callout>
    );
  }

  if (error.isRefusedTransition) {
    return (
      <Callout tone="warn" title="That transition was refused" live>
        <p>{error.message}</p>
      </Callout>
    );
  }

  return (
    <Callout tone="danger" title={`Request failed (HTTP ${error.status})`} live>
      <p>{error.message}</p>
    </Callout>
  );
}

export function EmptyState({
  title,
  children,
}: {
  title: string;
  children?: ReactNode;
}): ReactNode {
  return (
    <div className="empty">
      <strong>{title}</strong>
      {children ? <p>{children}</p> : null}
    </div>
  );
}

export function Loading({ label, rows = 3 }: { label: string; rows?: number }): ReactNode {
  return (
    <div className="panel-body" aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="skeleton" style={{ width: `${100 - index * 12}%` }} />
      ))}
    </div>
  );
}

export function Badge({
  children,
  tone,
  title,
}: {
  children: ReactNode;
  tone?: 'code' | 'good' | 'bad' | 'live';
  title?: string;
}): ReactNode {
  return (
    <span className={tone ? `badge badge-${tone}` : 'badge'} title={title}>
      {children}
    </span>
  );
}

/**
 * Claim status, coloured by whether it is still live.
 *
 * `paid` is the only good terminal state; `rejected`, `abandoned` and `expired`
 * all mean no money, and an operator scanning a list should not have to read
 * the word to know which of the two they are looking at.
 */
export function StatusBadge({ status }: { status: ClaimStatus }): ReactNode {
  const tone =
    status === 'paid'
      ? 'good'
      : status === 'rejected' || status === 'expired' || status === 'abandoned'
        ? 'bad'
        : 'live';
  return <Badge tone={tone}>{status.replace(/_/g, ' ')}</Badge>;
}

/**
 * The days-left cell.
 *
 * Three signals for one fact, because this is the number the whole product
 * exists to surface: a large tabular figure, a sentence naming the state, and a
 * tier colour. Any one of them alone would be enough to miss.
 */
export function UrgencyCell({
  daysLeft,
  expiresAt,
}: {
  daysLeft: number | null;
  expiresAt: string | null;
}): ReactNode {
  return (
    <div className="urgency">
      <span className="urgency-glyph" aria-hidden="true">
        {urgencyGlyph(daysLeft)}
      </span>
      <span>
        <span className="urgency-text">{urgencyText(daysLeft)}</span>
        <br />
        <span className="urgency-sub num">
          {expiresAt ? `expires ${formatDate(expiresAt)}` : 'no stated window'}
        </span>
      </span>
    </div>
  );
}

export function tierClass(daysLeft: number | null): string {
  return `u-${urgencyTier(daysLeft)}`;
}

/** Human names for the tiers, used on the filter chips and in the banner. */
export const TIER_LABEL: Record<UrgencyTier, string> = {
  lapsed: 'Lapsed',
  critical: '7 days or less',
  high: '8–14 days',
  medium: '15–21 days',
  low: 'Over 21 days',
  none: 'No deadline',
};

/**
 * A labelled form control.
 *
 * The control is a render prop so the generated id reaches it: a `<label>` that
 * is not bound to its input announces nothing to a screen reader and does not
 * focus the field when clicked, and the ops forms are where the operator is
 * typing the ticket id that is the only proof a claim was ever filed.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (control: { id: string; describedBy: string | undefined }) => ReactNode;
}): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {hint ? (
        <span className="field-hint" id={hintId}>
          {hint}
        </span>
      ) : null}
      {children({ id, describedBy })}
      {error ? (
        <span className="field-error" id={errorId} role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}
